/**
 * Backend API Service for Deals
 * Replaces Firebase direct calls with backend API calls
 */

import { DeepLinkConfig } from './DeepLinkService';

// Multi-slide support: each slide has paired mobile/desktop images and optional deep link
export interface ImageSlide {
  id: string;              // Unique identifier (UUID)
  mobile: string;          // Mobile image URL
  desktop: string;         // Desktop image URL
  deepLink?: DeepLinkConfig | null;   // Per-slide deep link
  regulatoryLink?: string | null;      // Optional external URL attached to this slide
  order: number;           // Display order (0-indexed)

}

export interface DealMemoData {
  dealType: 'text' | 'image';
  title?: string;
  content?: string;

  // LEGACY FORMAT - Keep for backward compatibility with existing single-image deals
  imageUrls?: {
    mobile: string;
    desktop: string;
  };
  deepLink?: DeepLinkConfig | null;   // Legacy single deep link
  regulatoryLink?: string | null;      // Legacy single external link URL

  // NEW FORMAT - Multi-slide support
  imageSlides?: ImageSlide[];

  // Carousel display order (lower numbers appear first)
  displayOrder?: number;

  // Pause flag: when true, deal is hidden from the public site without losing its config
  isPaused?: boolean;

  // Recurring series id: shared by every instance generated from a single recurring create.
  // Enables Google-Calendar-style bulk edit/delete (this / future / all).
  recurringSeriesId?: string;

  schedulingEnabled?: boolean;
  startDateTime?: Date;
  endDateTime?: Date;
  timezone?: string;
  lastEdited: Date | null;
  lastAuthor: string;
  editors: string[];
  isPublished: boolean;
}

export interface ScheduledDealData extends DealMemoData {
  documentId: string;
  sequenceNumber?: number;
  status: 'scheduled' | 'current' | 'expired';
  menuType?: 'med' | 'rec';  // Extracted from document ID for admin display
}

export interface StoreDealsData {
  storeId: string;
  storeName: string;
  currentDeals: DealMemoData[];  // Array of currently active deals (can overlap)
  scheduledDeals: ScheduledDealData[];
}

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL!;

/**
 * Get Firebase ID token from current user
 * Requires Firebase Auth to be initialized and user logged in
 */
const getAuthToken = async (): Promise<string> => {
  const { getAuth } = await import('firebase/auth');
  const auth = getAuth();
  if (!auth.currentUser) throw new Error('No authenticated user found');
  return await auth.currentUser.getIdToken();
};

/**
 * Get deal memo for admin editing (full data with metadata)
 */
export const getDealMemo = async (storeId: string): Promise<DealMemoData | null> => {
  try {
    const token = await getAuthToken();
    const requestUrl = `${API_BASE_URL}/api/deals/${storeId}/admin`;

    const response = await fetch(requestUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (result.success && result.data) {
      return {
        dealType: result.data.dealType || 'text',
        content: result.data.content,
        imageUrls: result.data.imageUrls,
        lastEdited: result.data.lastEdited ? new Date(result.data.lastEdited) : null,
        lastAuthor: result.data.lastAuthor,
        editors: result.data.editors,
        isPublished: result.data.isPublished,
        schedulingEnabled: result.data.schedulingEnabled,
        startDateTime: result.data.startDateTime ? new Date(result.data.startDateTime) : undefined,
        endDateTime: result.data.endDateTime ? new Date(result.data.endDateTime) : undefined,
        timezone: result.data.timezone,
      };
    }

    return null;
  } catch (error) {
    console.error('Error fetching deal memo from backend:', error);
    throw error;
  }
};

/**
 * Filter out undefined values from an object to prevent Firestore errors
 */
const filterUndefined = (obj: any): any => {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== undefined)
  );
};

/**
 * Save deal memo content
 */
export const saveDealMemo = async (
  storeId: string,
  dealData: {
    dealType: 'text' | 'image';
    content?: string;
    imageUrls?: { mobile: string; desktop: string };
  },
  isPublished: boolean = false
): Promise<void> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/deals/${storeId}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(filterUndefined({
        dealType: dealData.dealType,
        content: dealData.content,
        ...(dealData.imageUrls && { imageUrls: dealData.imageUrls }),
        isPublished,
        // Don't auto-add scheduling - let backend handle it properly
      })),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to save deal memo');
    }
  } catch (error) {
    console.error('Error saving deal memo to backend:', error);
    throw error;
  }
};

/**
 * Update publish status only
 */
export const updatePublishStatus = async (
  storeId: string,
  isPublished: boolean
): Promise<void> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/deals/${storeId}/publish`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        isPublished,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to update publish status');
    }
  } catch (error) {
    console.error('Error updating publish status via backend:', error);
    throw error;
  }
};

/**
 * Bulk-update fields across every member of a recurring series.
 * Scope: 'future' applies from instanceDocumentId's startDateTime onward; 'all' applies to every series member.
 * Note: startDateTime/endDateTime are intentionally not cascaded by the backend.
 */
export const updateScheduledDealSeries = async (
  storeId: string,
  seriesId: string,
  payload: {
    scope: 'future' | 'all';
    instanceDocumentId: string;
    title?: string;
    content?: string;
    imageUrls?: { mobile: string; desktop: string };
    imageSlides?: any[];
    deepLink?: DeepLinkConfig | null;
    regulatoryLink?: string | null;
  }
): Promise<{ updated: number }> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/api/deals/${storeId}/scheduled/series/${seriesId}`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    return { updated: result.updated ?? 0 };
  } catch (error) {
    console.error('Error bulk-updating series:', error);
    throw error;
  }
};

/**
 * Bulk-delete members of a recurring series within a scope.
 */
export const deleteScheduledDealSeries = async (
  storeId: string,
  seriesId: string,
  payload: {
    scope: 'future' | 'all';
    instanceDocumentId: string;
  }
): Promise<{ deleted: number }> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/api/deals/${storeId}/scheduled/series/${seriesId}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    return { deleted: result.deleted ?? 0 };
  } catch (error) {
    console.error('Error bulk-deleting series:', error);
    throw error;
  }
};

/**
 * Pause or resume a scheduled deal.
 * Paused deals stay in the admin queue but disappear from the public site.
 */
export const pauseScheduledDeal = async (
  storeId: string,
  documentId: string,
  isPaused: boolean
): Promise<void> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/api/deals/${storeId}/scheduled/${documentId}/pause`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isPaused }),
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to update pause status');
    }
  } catch (error) {
    console.error('Error updating pause status via backend:', error);
    throw error;
  }
};

/**
 * Get all deals for a store (current + scheduled)
 */
export const getAllStoreDeals = async (storeId: string): Promise<StoreDealsData> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/deals/${storeId}/scheduled`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (result.success && result.data) {
      // Convert date strings to Date objects for scheduled deals
      const scheduledDeals = result.data.scheduledDeals.map((deal: any) => ({
        ...deal,
        lastEdited: deal.lastEdited ? new Date(deal.lastEdited) : null,
        startDateTime: deal.startDateTime ? new Date(deal.startDateTime) : undefined,
        endDateTime: deal.endDateTime ? new Date(deal.endDateTime) : undefined,
      }));

      // Convert current deal dates
      const currentDeal = result.data.currentDeal ? {
        ...result.data.currentDeal,
        lastEdited: result.data.currentDeal.lastEdited ? new Date(result.data.currentDeal.lastEdited) : null,
        startDateTime: result.data.currentDeal.startDateTime ? new Date(result.data.currentDeal.startDateTime) : undefined,
        endDateTime: result.data.currentDeal.endDateTime ? new Date(result.data.currentDeal.endDateTime) : undefined,
      } : undefined;

      return {
        storeId: result.data.storeId,
        storeName: result.data.storeName,
        currentDeal,
        scheduledDeals
      };
    }

    throw new Error('Invalid response format');
  } catch (error) {
    console.error('Error fetching all store deals:', error);
    throw error;
  }
};

/**
 * Create a new scheduled deal
 */
export const createScheduledDeal = async (
  storeId: string,
  dealData: {
    dealType: 'text' | 'image';
    content?: string;
    imageUrls?: { mobile: string; desktop: string };
    deepLink?: DeepLinkConfig | null;
    regulatoryLink?: string | null;
    startDateTime: Date;
    endDateTime: Date;
  }
): Promise<void> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/deals/${storeId}/scheduled`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(filterUndefined({
        dealType: dealData.dealType,
        content: dealData.content,
        ...(dealData.imageUrls && { imageUrls: dealData.imageUrls }),
        deepLink: dealData.deepLink,
        regulatoryLink: dealData.regulatoryLink,
        startDateTime: dealData.startDateTime.toISOString(),
        endDateTime: dealData.endDateTime.toISOString(),
      })),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to create scheduled deal');
    }
  } catch (error) {
    console.error('Error creating scheduled deal:', error);
    throw error;
  }
};

/**
 * Update an existing scheduled deal
 */
export const updateScheduledDeal = async (
  storeId: string,
  documentId: string,
  dealData: {
    dealType: 'text' | 'image';
    content?: string;
    imageUrls?: { mobile: string; desktop: string };
    deepLink?: DeepLinkConfig | null;
    regulatoryLink?: string | null;
    startDateTime: Date;
    endDateTime: Date;
  }
): Promise<void> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/deals/${storeId}/scheduled/${documentId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(filterUndefined({
        dealType: dealData.dealType,
        content: dealData.content,
        ...(dealData.imageUrls && { imageUrls: dealData.imageUrls }),
        deepLink: dealData.deepLink,
        regulatoryLink: dealData.regulatoryLink,
        startDateTime: dealData.startDateTime.toISOString(),
        endDateTime: dealData.endDateTime.toISOString(),
      })),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to update scheduled deal');
    }
  } catch (error) {
    console.error('Error updating scheduled deal:', error);
    throw error;
  }
};

/**
 * Delete a scheduled deal
 */
export const deleteScheduledDeal = async (storeId: string, documentId: string): Promise<void> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/deals/${storeId}/scheduled/${documentId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to delete scheduled deal');
    }
  } catch (error) {
    console.error('Error deleting scheduled deal:', error);
    throw error;
  }
};

/**
 * Update display order for active deals in the carousel
 */
export const reorderDeals = async (
  storeId: string,
  dealOrder: Array<{ documentId: string; displayOrder: number }>
): Promise<void> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/deals/${storeId}/reorder`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ dealOrder }),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to reorder deals');
    }
  } catch (error) {
    console.error('Error reordering deals:', error);
    throw error;
  }
};

/**
 * Create a scheduled image deal with integrated upload
 */
export const createScheduledImageDeal = async (
  storeId: string,
  mobileImage: File,
  desktopImage: File,
  startDateTime: Date,
  endDateTime: Date,
  deepLink?: DeepLinkConfig | null,
  regulatoryLink?: string | null,
  title?: string,
  recurringSeriesId?: string,
): Promise<{ documentId: string; imageUrls: { mobile: string; desktop: string } }> => {
  try {
    const token = await getAuthToken();
    const formData = new FormData();
    formData.append('mobileImage', mobileImage);
    formData.append('desktopImage', desktopImage);
    formData.append('content', ''); // Empty string for image deals to avoid Firestore undefined error
    formData.append('startDateTime', startDateTime.toISOString());
    formData.append('endDateTime', endDateTime.toISOString());
    if (deepLink) {
      formData.append('deepLink', JSON.stringify(deepLink));
    }
    if (regulatoryLink) {
      formData.append('regulatoryLink', regulatoryLink);
    }
    if (title) {
      formData.append('title', title);
    }
    if (recurringSeriesId) {
      formData.append('recurringSeriesId', recurringSeriesId);
    }
    const response = await fetch(`${API_BASE_URL}/api/deals/${storeId}/upload-and-schedule`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to create scheduled image deal');
    }

    return {
      documentId: result.data.documentId,
      imageUrls: result.data.imageUrls
    };
  } catch (error) {
    console.error('Error creating scheduled image deal:', error);
    throw error;
  }
};

/**
 * Update a scheduled image deal
 */
export const updateScheduledImageDeal = async (
  storeId: string,
  documentId: string,
  updates: {
    mobileImage?: File;
    desktopImage?: File;
    startDateTime?: Date;
    endDateTime?: Date;
    deepLink?: DeepLinkConfig | null;
    regulatoryLink?: string | null;
    title?: string;
    [key: string]: any; // Allow extra fields passed from admin portal
  }
): Promise<{ imageUrls: { mobile: string; desktop: string } }> => {
  try {
    const token = await getAuthToken();
    const formData = new FormData();

    formData.append('content', ''); // Empty string for image deals to avoid Firestore undefined error
    if (updates.mobileImage) formData.append('mobileImage', updates.mobileImage);
    if (updates.desktopImage) formData.append('desktopImage', updates.desktopImage);
    if (updates.startDateTime) formData.append('startDateTime', updates.startDateTime.toISOString());
    if (updates.endDateTime) formData.append('endDateTime', updates.endDateTime.toISOString());
    if (updates.deepLink !== undefined) {
      formData.append('deepLink', JSON.stringify(updates.deepLink));
    }
    if (updates.regulatoryLink !== undefined) {
      // Allow null to clear the regulatory link
      formData.append('regulatoryLink', updates.regulatoryLink || '');
    }
    if (updates.title !== undefined) {
      formData.append('title', updates.title);
    }
    const response = await fetch(`${API_BASE_URL}/api/deals/${storeId}/scheduled/${documentId}/update-images`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to update scheduled image deal');
    }

    return {
      imageUrls: result.data.imageUrls
    };
  } catch (error) {
    console.error('Error updating scheduled image deal:', error);
    throw error;
  }
};